/**
 * ResidualRiskPhase — merged Residual Risks & Decisions stop.
 *
 * Collapses the former two rail stops (`risks` scoring view +
 * `decisions` treatment view) into ONE stop rendering a single
 * DataTable of every residual risk. The same `state.data.risks`
 * array drives both the score columns (Impact / Feasibility /
 * Risk Level deltas) and the treatment column.
 *
 * Two derived backend-phase flags gate behaviour:
 *   • isAnalysing = backendPhase === 'risk_analysis'
 *       The scoring agent is still running. Show a spinner block
 *       (the table has no settled scores yet) and 3s polling in
 *       `useResidualProgress` advances it to risk_decisions.
 *   • editable    = backendPhase === 'risk_decisions'
 *       The user records a treatment per risk via the existing
 *       `TreatmentModal` (opened from the row action). Any other
 *       phase (loop-back to controls, completed, or a past-phase
 *       rail jump) is read-only — no action column, no Complete CTA.
 *
 * The treatment chooser is the existing TreatmentModal, unchanged —
 * all four/six submit shapes flow straight through
 * `actions.submitDecision`. The claim / control / goal pools the
 * modal needs are derived here exactly as the old RiskDecisionsPhase
 * did (carried over verbatim).
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../../../../../components/ui/Button2';
import { LoadingState } from '../../../../../components/ui/LoadingState';
import { TypographyH4, TypographyMuted } from '../../../../../components/ui/Typography';
import DataTableComponent from '../../../../../components/ui/DataTable/DataTable';
import RowActionsMenu from '../../../../../components/ui/DataTable/RowActionsMenu';
import LevelBadgeComponent from '../../../../../components/ui/LevelBadge';
import CategoryPillComponent from '../../../../../components/ui/CategoryPill';
import {
  getFeasibilityColor,
  getRiskValueColor,
} from '../../../../../utils/severityStyle';
import TreatmentModal from '../../../../athena/components/TreatmentModal';
import { useWorkflowResults } from '../../../../athena/contexts/WorkflowResultsContext';
import type { ResidualActions } from '../../../hooks/useResidualProgress';
import type { ResidualRisk, ResidualState } from '../../../state/types';
import AnalysisErrorCard from '../../AnalysisErrorCard/AnalysisErrorCard';
import styles from './ResidualRiskPhase.module.css';

interface ResidualRiskPhaseProps {
  state: ResidualState;
  actions: ResidualActions;
}

/* DataTable is an untyped JS primitive — `tsc` infers its default-less
 * props (onSortChange, onRowClick, …) as required. We use only the
 * read-only subset, so narrow the import to the props we pass. */
interface DataTableColumn {
  key: string;
  label: string;
  /** Percentage string (canonical, e.g. '32%') or pixel number. */
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  render?: (row: ResidualRisk) => React.ReactNode;
}
const DataTable = DataTableComponent as unknown as React.FC<{
  columns: DataTableColumn[];
  data: ResidualRisk[];
  rowKey?: string;
  emptyMessage?: string;
  className?: string;
}>;

/* LevelBadge / CategoryPill are untyped JS primitives — `tsc` infers
 * their default-less props (color / textColor) as required. Narrow to
 * the optional-prop shape we actually use, matching their JS runtime. */
const LevelBadge = LevelBadgeComponent as unknown as React.FC<{
  color?: string;
  textColor?: string;
  label: React.ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}>;
const CategoryPill = CategoryPillComponent as unknown as React.FC<{
  label: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info';
  color?: string;
  size?: 'sm' | 'md';
  className?: string;
}>;

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;

type Treatment = 'Avoid' | 'Reduce' | 'Share' | 'Retain';

/* Treatment → CategoryPill tone, preserving the current per-treatment
 * color scheme (Avoid red, Reduce blue, Share yellow, Retain gray). */
const TREATMENT_TONE: Record<Treatment, 'error' | 'info' | 'warning' | 'neutral'> = {
  Avoid: 'error',
  Reduce: 'info',
  Share: 'warning',
  Retain: 'neutral',
};

/* ── Score helpers ───────────────────────────────────────────────── */

/* Lower risk number = safer. Used for the header Lower/Higher stats. */
const compareScore = (
  prev: number | undefined | null,
  curr: number | undefined | null,
): 'improved' | 'worsened' | 'unchanged' => {
  if (prev == null || curr == null) return 'unchanged';
  if (curr < prev) return 'improved';
  if (curr > prev) return 'worsened';
  return 'unchanged';
};

const pickNumber = (risk: ResidualRisk, ...keys: string[]): number | undefined => {
  const r = risk as Record<string, unknown>;
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
};

const pickString = (risk: ResidualRisk, ...keys: string[]): string | undefined => {
  const r = risk as Record<string, unknown>;
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
};

const isDecided = (risk: ResidualRisk): boolean =>
  typeof risk.current_treatment === 'string' && risk.current_treatment.length > 0;

/* Condense a decided risk's detail block (claim / control / new
 * requirement) into a single muted summary line for the table cell.
 * Full detail still lives behind the treatment flow. */
const decisionSummary = (
  risk: ResidualRisk,
  newClaims: Array<Record<string, unknown>>,
  sessionClaims: Array<Record<string, unknown>>,
): string | null => {
  const r = risk as Record<string, unknown>;

  const linkedClaimId = r.linked_claim_id as string | undefined;
  const linkedClaim =
    (r.new_claim as Record<string, unknown> | undefined) ??
    (linkedClaimId
      ? newClaims.find((c) => c.claim_id === linkedClaimId) ??
        sessionClaims.find((c) => c.claim_id === linkedClaimId)
      : undefined);
  if (linkedClaim?.claim_id) return `Claim ${linkedClaim.claim_id as string}`;

  const newControl = r.new_control as Record<string, unknown> | undefined;
  if (newControl?.control_id) {
    return `New control ${newControl.control_id as string}${
      newControl.control_name ? `: ${newControl.control_name as string}` : ''
    }`;
  }

  const newRequirement = r.new_requirement as
    | { control_id?: string; requirement?: Record<string, unknown> }
    | undefined;
  if (newRequirement?.requirement?.req_statement) {
    return `New requirement on ${newRequirement.control_id ?? 'control'}`;
  }

  return null;
};

export default function ResidualRiskPhase({ state, actions }: ResidualRiskPhaseProps) {
  const data = state.data;
  const risks = data?.risks ?? [];
  const controls = data?.controls ?? [];
  const newClaims = (data?.new_claims as Array<Record<string, unknown>>) ?? [];
  const goals = ((data as Record<string, unknown>)?.goals as
    | Array<Record<string, unknown>>
    | undefined) ?? [];

  const isAnalysing = state.backendPhase === 'risk_analysis';
  const editable = state.backendPhase === 'risk_decisions';

  const { sessionData } = useWorkflowResults() as {
    sessionData: {
      cybersecurityClaims?: Array<Record<string, unknown>>;
      cybersecurityGoals?: Array<Record<string, unknown>>;
    };
  };
  const sessionClaims = sessionData?.cybersecurityClaims ?? [];

  const [selectedRisk, setSelectedRisk] = useState<ResidualRisk | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);

  /* ── Modal-feeding pools (carried over from RiskDecisionsPhase so
   *    the existing TreatmentModal sees the same selectables). ──── */
  const provisionalControls = useMemo(
    () =>
      risks
        .filter(
          (r) =>
            (r as Record<string, unknown>).new_control &&
            r.risk_id !== selectedRisk?.risk_id,
        )
        .map((r) => (r as Record<string, unknown>).new_control),
    [risks, selectedRisk],
  );

  const existingClaims = useMemo(() => {
    const allBase = [...sessionClaims, ...newClaims];
    const otherRiskClaims = risks
      .filter(
        (r) =>
          (r as Record<string, unknown>).new_claim &&
          r.risk_id !== selectedRisk?.risk_id,
      )
      .map((r) => (r as Record<string, unknown>).new_claim as Record<string, unknown>)
      .filter((claim) => {
        const claimId = claim?.claim_id as string | undefined;
        return (
          claimId &&
          !allBase.find((c) => (c as Record<string, unknown>).claim_id === claimId)
        );
      });
    return [...allBase, ...otherRiskClaims];
  }, [sessionClaims, newClaims, risks, selectedRisk]);

  const goalsForModal = useMemo(
    () => (goals.length > 0 ? goals : sessionData?.cybersecurityGoals ?? []),
    [goals, sessionData],
  );

  /* ── Stats — merge both former phases' summaries. ────────────── */
  const stats = useMemo(() => {
    let improved = 0;
    let worsened = 0;
    let decided = 0;
    let reduce = 0;
    for (const risk of risks) {
      const cmp = compareScore(
        pickNumber(risk, 'previous_risk_level', 'previous_risk_score'),
        pickNumber(risk, 'current_risk_level', 'current_risk_score'),
      );
      if (cmp === 'improved') improved += 1;
      else if (cmp === 'worsened') worsened += 1;

      if (isDecided(risk)) {
        decided += 1;
        if ((risk.current_treatment as Treatment) === 'Reduce') reduce += 1;
      }
    }
    return { total: risks.length, improved, worsened, decided, reduce };
  }, [risks]);

  const allDecided = stats.total > 0 && stats.decided === stats.total;

  // Name what's blocking the CTA — with 30 rows a single "Pending"
  // treatment cell is easy to miss, and a bare disabled button explains
  // nothing (staging session 9099dc3c sat blocked on one undecided risk).
  const pendingRiskIds = useMemo(
    () => risks.filter((r) => !isDecided(r)).map((r) => r.risk_id),
    [risks],
  );

  // TreatmentModal (FormModal-based) owns the submit lifecycle: a rejection
  // here surfaces as its inline error and keeps it open; on success it calls
  // onClose, which clears selectedRisk.
  const handleSubmit = async (decisionData: unknown) => {
    if (!selectedRisk) return;
    await actions.submitDecision(selectedRisk.risk_id, decisionData);
  };

  const handleContinue = async () => {
    if (isContinuing) return;
    setIsContinuing(true);
    try {
      await actions.continueWorkflow();
    } finally {
      setIsContinuing(false);
    }
  };

  /* ── Columns ─────────────────────────────────────────────────── */
  const columns = useMemo(
    () => [
      {
        key: 'risk_id',
        label: 'ID',
        width: '8%',
        render: (risk: ResidualRisk) => (
          <span className={styles.idChip}>{risk.risk_id}</span>
        ),
      },
      {
        key: 'risk',
        label: 'Risk',
        width: '32%',
        render: (risk: ResidualRisk) => (
          <div className={styles.riskIdent}>
            <span className={styles.riskTitle}>
              {risk.threat_name || risk.threat_id || 'Risk'}
            </span>
            {(risk.asset_name || risk.asset_id) && (
              <span className={styles.assetTag}>
                on {risk.asset_name || risk.asset_id}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'impact',
        label: 'Impact',
        width: '8%',
        render: (risk: ResidualRisk) => (
          <span className={styles.impactValue}>{risk.impact_rating ?? '—'}</span>
        ),
      },
      {
        key: 'feasibility',
        label: 'Feasibility',
        width: '17%',
        render: (risk: ResidualRisk) => {
          const prevRating = pickString(
            risk,
            'previous_feasibility_rating',
            'previous_attack_feasibility_rating',
          );
          const currRating = pickString(
            risk,
            'current_feasibility_rating',
            'current_attack_feasibility_rating',
          );
          return (
            <div className={styles.deltaCell}>
              {prevRating ? (
                <LevelBadge size="sm" color={getFeasibilityColor(prevRating)} label={prevRating} />
              ) : (
                <span className={styles.mutedValue}>—</span>
              )}
              <span className={styles.deltaArrow}>→</span>
              {currRating ? (
                <LevelBadge size="sm" color={getFeasibilityColor(currRating)} label={currRating} />
              ) : (
                <span className={styles.mutedValue}>Pending</span>
              )}
            </div>
          );
        },
      },
      {
        key: 'risk_level',
        label: 'Risk Level',
        width: '17%',
        render: (risk: ResidualRisk) => {
          const prevRating = pickString(risk, 'previous_risk_rating');
          const currRating = pickString(risk, 'current_risk_rating');
          const prevLevel = pickNumber(risk, 'previous_risk_level', 'previous_risk_score');
          const currLevel = pickNumber(risk, 'current_risk_level', 'current_risk_score');
          const fmt = (rating: string, lvl: number | undefined) =>
            `${rating}${lvl != null ? ` (${lvl})` : ''}`;
          return (
            <div className={styles.deltaCell}>
              {prevRating ? (
                <LevelBadge
                  size="sm"
                  color={getRiskValueColor(prevLevel as number) ?? undefined}
                  label={fmt(prevRating, prevLevel)}
                />
              ) : (
                <span className={styles.mutedValue}>—</span>
              )}
              <span className={styles.deltaArrow}>→</span>
              {currRating ? (
                <LevelBadge
                  size="sm"
                  color={getRiskValueColor(currLevel as number) ?? undefined}
                  label={fmt(currRating, currLevel)}
                />
              ) : (
                <span className={styles.mutedValue}>Pending</span>
              )}
            </div>
          );
        },
      },
      {
        key: 'treatment',
        label: 'Treatment',
        width: '13%',
        render: (risk: ResidualRisk) => {
          if (!isDecided(risk)) {
            return <span className={styles.mutedValue}>Pending</span>;
          }
          const treatment = risk.current_treatment as Treatment;
          const summary = decisionSummary(risk, newClaims, sessionClaims);
          return (
            <div className={styles.treatmentCell}>
              <CategoryPill size="sm" tone={TREATMENT_TONE[treatment]} label={treatment} />
              {summary && <span className={styles.treatmentSummary}>{summary}</span>}
            </div>
          );
        },
      },
      {
        key: '_actions',
        label: '',
        align: 'right' as const,
        width: '5%',
        render: (risk: ResidualRisk) => {
          if (!editable) return null;
          return (
            <RowActionsMenu
              ariaLabel={`Treatment for ${risk.risk_id}`}
              actions={[
                {
                  id: 'choose',
                  label: isDecided(risk) ? 'Change Treatment' : 'Choose Treatment',
                  onClick: () => setSelectedRisk(risk),
                },
              ]}
            />
          );
        },
      },
    ],
    [editable, newClaims, sessionClaims],
  );

  return (
    <div className={styles.phase}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <TypographyH4>Residual Risks &amp; Decisions</TypographyH4>
          <TypographyMuted className={styles.subtitle}>
            How each risk&rsquo;s level shifted after the agent applied the
            updated attack-path feasibility, and the treatment you record for
            it. Impact stays anchored to the base TARA&mdash;only feasibility
            (and therefore risk level) move. Reduce decisions send the workflow
            back to Controls Implementation; anything else moves on to
            Completion.
          </TypographyMuted>
        </div>
        <div className={styles.statsBlock}>
          <span className={styles.statsItem}>
            <strong>{stats.improved}</strong> Lower
          </span>
          <span className={styles.statsItem}>
            <strong>{stats.worsened}</strong> Higher
          </span>
          <span className={styles.statsItem}>
            <strong>{stats.decided}</strong> Decided
          </span>
          {stats.reduce > 0 && (
            <span className={styles.statsItem}>
              <strong>{stats.reduce}</strong> Reduce
            </span>
          )}
          <span className={styles.statsTotal}>
            of {stats.total} risk{stats.total === 1 ? '' : 's'}
          </span>
        </div>
      </header>

      {isAnalysing ? (
        state.analysisError ? (
          <AnalysisErrorCard
            title="Residual risk recompute failed"
            message={state.analysisError}
            onRetry={actions.continueWorkflow}
          />
        ) : (
          <LoadingStateAny
            variant="page"
            title="Recomputing residual risk&hellip;"
            description="The agent is scoring each risk against the new feasibility ratings. This usually finishes in under a minute."
          />
        )
      ) : (
        <DataTable
          columns={columns}
          data={risks}
          rowKey="risk_id"
          emptyMessage="No risk data available yet for this session."
          className={styles.flushTable}
        />
      )}

      {editable && (
        <footer className={styles.footer}>
          {!allDecided && pendingRiskIds.length > 0 && (
            <TypographyMuted className={styles.footerHint}>
              {pendingRiskIds.length === 1
                ? `1 risk still needs a treatment decision: ${pendingRiskIds[0]}`
                : `${pendingRiskIds.length} risks still need a treatment decision: ${pendingRiskIds
                    .slice(0, 5)
                    .join(', ')}${pendingRiskIds.length > 5 ? '…' : ''}`}
            </TypographyMuted>
          )}
          <ButtonAny
            size="lg"
            loading={isContinuing}
            onClick={handleContinue}
            disabled={isContinuing || !allDecided}
          >
            {isContinuing ? 'Submitting…' : 'Complete Workflow'}
          </ButtonAny>
        </footer>
      )}

      {selectedRisk && (
        <TreatmentModal
          isOpen={!!selectedRisk}
          onClose={() => setSelectedRisk(null)}
          risk={selectedRisk}
          controls={controls}
          provisionalControls={provisionalControls}
          goals={goalsForModal}
          existingClaims={existingClaims}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
